import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { TrendingUp, Building, DollarSign, Calendar, MapPin, Crown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { getBookingAmount } from '../utils/formatters';

const Analytics = () => {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    userGrowth: [],
    propertyTypes: [],
    revenueByMonth: [],
    bookingsByRegion: [],
    topProperties: [],
    summary: {
      totalBookings: 0,
      totalRevenue: 0,
    }
  });

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      // Fetch real data from Firestore
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const propertiesSnapshot = await getDocs(collection(db, 'properties'));
      const bookingsSnapshot = await getDocs(collection(db, 'bookings'));

      // Process user growth by month
      const usersByMonth = {};
      usersSnapshot.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate?.() || new Date();
        const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        if (!usersByMonth[monthKey]) {
          usersByMonth[monthKey] = { total: 0, hosts: 0, guests: 0 };
        }
        usersByMonth[monthKey].total++;
        if (data.role === 'host') usersByMonth[monthKey].hosts++;
        else usersByMonth[monthKey].guests++;
      });

      // Process property types
      const typeCounts = { apartment: 0, house: 0, villa: 0, room: 0 };
      propertiesSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.type && typeCounts[data.type] !== undefined) {
          typeCounts[data.type]++;
        }
      });

      const propertyTypes = [
        { name: 'Apartment', value: typeCounts.apartment, color: '#3B82F6' },
        { name: 'House', value: typeCounts.house, color: '#22C55E' },
        { name: 'Villa', value: typeCounts.villa, color: '#F59E0B' },
        { name: 'Room', value: typeCounts.room, color: '#8B5CF6' },
      ].filter(item => item.value > 0);

      // Process bookings by month and region
      const bookingsByMonth = {};
      const bookingsByRegion = {};
      const propertyBookings = {};
      const bookingSummary = {
        totalBookings: 0,
        totalRevenue: 0,
      };

      bookingsSnapshot.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate?.() || new Date();
        const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        const bookingAmount = getBookingAmount(data);

        bookingSummary.totalBookings++;
        bookingSummary.totalRevenue += bookingAmount;

        if (!bookingsByMonth[monthKey]) {
          bookingsByMonth[monthKey] = { bookings: 0, revenue: 0 };
        }
        bookingsByMonth[monthKey].bookings++;
        bookingsByMonth[monthKey].revenue += bookingAmount;

        // Track property bookings
        if (data.propertyId) {
          if (!propertyBookings[data.propertyId]) {
            propertyBookings[data.propertyId] = { count: 0, revenue: 0 };
          }
          propertyBookings[data.propertyId].count++;
          propertyBookings[data.propertyId].revenue += bookingAmount;
        }
      });

      // Process properties by region
      propertiesSnapshot.forEach(doc => {
        const data = doc.data();
        const region = data.address?.city || 'Unknown';
        if (!bookingsByRegion[region]) {
          bookingsByRegion[region] = { bookings: 0, revenue: 0, properties: 0 };
        }
        bookingsByRegion[region].properties++;
      });

      // Convert to chart data
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const revenueData = [];
      const userGrowthData = [];

      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthData = bookingsByMonth[monthKey] || { bookings: 0, revenue: 0 };
        const userData = usersByMonth[monthKey] || { total: 0, hosts: 0, guests: 0 };

        revenueData.push({
          month: months[date.getMonth()],
          bookings: monthData.bookings,
          revenue: monthData.revenue
        });

        userGrowthData.push({
          month: months[date.getMonth()],
          users: userData.total,
          hosts: userData.hosts,
          guests: userData.guests
        });
      }

      // Get top properties
      const topProperties = [];
      for (const [propertyId, stats] of Object.entries(propertyBookings)) {
        const propertyDoc = propertiesSnapshot.docs.find(d => d.id === propertyId);
        if (propertyDoc) {
          const propertyData = propertyDoc.data();
          topProperties.push({
            title: propertyData.title || 'Untitled',
            location: propertyData.address?.city || 'Unknown Location',
            bookings: stats.count,
            revenue: stats.revenue,
            rating: propertyData.rating || 0
          });
        }
      }
      topProperties.sort((a, b) => b.revenue - a.revenue);

      // Convert regions to array
      const regionData = Object.entries(bookingsByRegion)
        .map(([region, data]) => ({
          region,
          ...data
        }))
        .sort((a, b) => b.properties - a.properties)
        .slice(0, 5);

      setAnalytics({
        userGrowth: userGrowthData,
        propertyTypes,
        revenueByMonth: revenueData,
        bookingsByRegion: regionData,
        topProperties: topProperties.slice(0, 5),
        summary: bookingSummary,
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        className="px-4 py-3 rounded-xl shadow-lg"
        style={{
          backgroundColor: colors.surfaceElevated,
          border: `1px solid ${colors.border}`,
        }}
      >
        <p className="text-xs font-semibold mb-1" style={{ color: colors.text }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs flex items-center gap-2" style={{ color: entry.stroke || entry.fill }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.stroke || entry.fill }} />
            {entry.name}: <span className="font-semibold" style={{ color: colors.text }}>{entry.value.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="skeleton h-8 w-48 mb-2" style={{ backgroundColor: colors.border }} />
          <div className="skeleton h-4 w-72" style={{ backgroundColor: colors.border }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl p-6 h-[140px]" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div className="skeleton h-4 w-24 mb-4" style={{ backgroundColor: colors.border }} />
              <div className="skeleton h-7 w-16" style={{ backgroundColor: colors.border }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const totalRevenue = analytics.summary.totalRevenue;
  const totalBookings = analytics.summary.totalBookings;
  const avgBookingValue = totalBookings > 0 ? Math.round(totalRevenue / totalBookings) : 0;

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: `${totalRevenue.toLocaleString()} SAR`,
      icon: TrendingUp,
      gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
      change: '+12.5%',
      isPositive: true
    },
    {
      title: 'Total Bookings',
      value: totalBookings.toLocaleString(),
      icon: Calendar,
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      change: '+4.2%',
      isPositive: true
    },
    {
      title: 'Avg. Booking Value',
      value: `${avgBookingValue.toLocaleString()} SAR`,
      icon: DollarSign,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      change: '-1.1%',
      isPositive: false
    },
    {
      title: 'Property Types',
      value: analytics.propertyTypes.length,
      icon: Building,
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      change: 'Active',
      isPositive: true,
      hideArrow: true
    }
  ];

  const exportReport = () => {
    const rows = [];

    // Summary section
    rows.push(['=== PLATFORM ANALYTICS REPORT ===']);
    rows.push([`Generated: ${new Date().toLocaleString()}`]);
    rows.push([]);

    rows.push(['--- SUMMARY ---']);
    rows.push(['Metric', 'Value']);
    rows.push(['Total Revenue (SAR)', analytics.summary.totalRevenue]);
    rows.push(['Total Bookings', analytics.summary.totalBookings]);
    rows.push(['Avg Booking Value (SAR)', totalBookings > 0 ? Math.round(analytics.summary.totalRevenue / analytics.summary.totalBookings) : 0]);
    rows.push([]);

    // Monthly revenue
    rows.push(['--- MONTHLY REVENUE & BOOKINGS (Last 6 Months) ---']);
    rows.push(['Month', 'Bookings', 'Revenue (SAR)']);
    analytics.revenueByMonth.forEach(r => rows.push([r.month, r.bookings, r.revenue]));
    rows.push([]);

    // User growth
    rows.push(['--- USER GROWTH (Last 6 Months) ---']);
    rows.push(['Month', 'Total Users', 'Hosts', 'Guests']);
    analytics.userGrowth.forEach(r => rows.push([r.month, r.users, r.hosts, r.guests]));
    rows.push([]);

    // Property types
    rows.push(['--- PROPERTY TYPE DISTRIBUTION ---']);
    rows.push(['Type', 'Count']);
    analytics.propertyTypes.forEach(r => rows.push([r.name, r.value]));
    rows.push([]);

    // Regional
    rows.push(['--- REGIONAL PERFORMANCE ---']);
    rows.push(['Region', 'Properties', 'Bookings', 'Revenue (SAR)']);
    analytics.bookingsByRegion.forEach(r => rows.push([r.region, r.properties, r.bookings, r.revenue]));
    rows.push([]);

    // Top properties
    rows.push(['--- TOP PERFORMING PROPERTIES ---']);
    rows.push(['Rank', 'Title', 'Location', 'Bookings', 'Revenue (SAR)', 'Rating']);
    analytics.topProperties.forEach((p, i) => rows.push([i + 1, p.title, p.location, p.bookings, p.revenue, p.rating.toFixed(1)]));

    const csv = rows.map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Analytics Overview
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Deep dive into your platform's performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportReport}
            className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors"
            style={{ backgroundColor: colors.surfaceHover, color: colors.text, border: `1px solid ${colors.border}` }}
            onMouseEnter={e => e.currentTarget.style.borderColor = colors.primaryBorder}
            onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
          >
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className={`card-premium p-5 opacity-0 animate-fadeInScale stagger-${index + 1}`}
            style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="flex items-center justify-center w-11 h-11 rounded-xl shadow-sm"
                style={{ background: card.gradient }}
              >
                <card.icon className="w-5 h-5 text-white" />
              </div>
            </div>

            <p className="text-[13px] font-medium mb-1" style={{ color: colors.textSecondary }}>
              {card.title}
            </p>
            <p className="text-[24px] font-bold tracking-tight" style={{ color: colors.text }}>
              {card.value}
            </p>

            <div className="flex items-center gap-1.5 mt-3">
              <span
                className="flex items-center gap-0.5 text-[12px] font-semibold px-1.5 py-0.5 rounded-md"
                style={{
                  backgroundColor: card.isPositive ? colors.successGhost : colors.errorGhost,
                  color: card.isPositive ? colors.success : colors.error
                }}
              >
                {!card.hideArrow && (card.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
                {card.change}
              </span>
              {!card.hideArrow && (
                <span className="text-[11px]" style={{ color: colors.textMuted }}>vs last month</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid - First Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        {/* User Growth Area Chart */}
        <div className="card-premium p-5" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>User Growth</h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Total registered users over time</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={analytics.userGrowth} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="userGrowthGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} vertical={false} />
              <XAxis dataKey="month" stroke={colors.textMuted} fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke={colors.textMuted} fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="users"
                name="Total Users"
                stroke="#3B82F6"
                strokeWidth={2.5}
                fill="url(#userGrowthGradient)"
                dot={{ fill: '#3B82F6', r: 4, strokeWidth: 2, stroke: colors.card }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: colors.card }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue vs Bookings */}
        <div className="card-premium p-5" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>Revenue & Bookings</h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Comparative trend analysis</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={analytics.revenueByMonth} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="revBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={1} />
                  <stop offset="100%" stopColor="#16A34A" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} vertical={false} />
              <XAxis dataKey="month" stroke={colors.textMuted} fontSize={11} tickLine={false} axisLine={false} />
              <YAxis yAxisId="left" stroke={colors.textMuted} fontSize={11} tickLine={false} axisLine={false} />
              <YAxis yAxisId="right" orientation="right" stroke={colors.textMuted} fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar yAxisId="left" name="Revenue (SAR)" dataKey="revenue" fill="url(#revBarGradient)" radius={[4, 4, 0, 0]} maxBarSize={32} />
              <Line yAxisId="right" name="Bookings" type="monotone" dataKey="bookings" stroke="#8B5CF6" strokeWidth={3} dot={{ fill: '#8B5CF6', r: 4 }} activeDot={{ r: 6 }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Grid - Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-5">
        {/* Regional Performance */}
        <div className="card-premium p-5 lg:col-span-3" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>Regional Performance</h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Top locations by property count</p>
            </div>
            <div className="p-2 rounded-lg" style={{ backgroundColor: colors.primaryGhost }}>
              <MapPin className="w-5 h-5" style={{ color: colors.primary }} />
            </div>
          </div>
          {analytics.bookingsByRegion.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics.bookingsByRegion} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="regionGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#60A5FA" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} horizontal={false} />
                <XAxis type="number" stroke={colors.textMuted} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis dataKey="region" type="category" stroke={colors.text} fontSize={12} fontWeight={500} tickLine={false} axisLine={false} width={100} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: colors.surfaceHover }} />
                <Bar name="Properties" dataKey="properties" fill="url(#regionGradient)" radius={[0, 6, 6, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[260px]">
              <MapPin className="w-10 h-10 mb-3" style={{ color: colors.textMuted }} />
              <p className="text-sm font-medium" style={{ color: colors.text }}>No regional data</p>
            </div>
          )}
        </div>

        {/* Property Types */}
        <div className="card-premium p-5 lg:col-span-2" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>Property Mix</h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Category distribution</p>
            </div>
          </div>
          {analytics.propertyTypes.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={analytics.propertyTypes}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {analytics.propertyTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {analytics.propertyTypes.map((entry, index) => (
                  <div key={index} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: colors.backgroundSecondary }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.color }} />
                      <span className="text-[12px] font-medium truncate" style={{ color: colors.textSecondary }}>{entry.name}</span>
                    </div>
                    <span className="text-[12px] font-bold" style={{ color: colors.text }}>{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[260px]">
              <Building className="w-10 h-10 mb-3" style={{ color: colors.textMuted }} />
              <p className="text-sm font-medium" style={{ color: colors.text }}>No property data</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Properties Table */}
      {analytics.topProperties.length > 0 && (
        <div className="card-premium" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="p-5" style={{ borderBottom: `1px solid ${colors.border}` }}>
            <h3 className="text-[15px] font-semibold flex items-center gap-2" style={{ color: colors.text }}>
              <Crown className="w-5 h-5 text-yellow-500" /> Top Performing Properties
            </h3>
            <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Properties generating the highest total revenue</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Property Details</th>
                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Location</th>
                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Bookings</th>
                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Revenue (SAR)</th>
                  <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-center" style={{ color: colors.textMuted }}>Rating</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topProperties.map((property, index) => (
                  <tr
                    key={index}
                    className="group transition-colors"
                    style={{ borderBottom: index < analytics.topProperties.length - 1 ? `1px solid ${colors.border}` : 'none' }}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-4">
                        <div
                          className="flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center font-bold text-white text-sm"
                          style={{ background: '#3B82F6' }}
                        >
                          #{index + 1}
                        </div>
                        <div>
                          <div className="text-[14px] font-semibold" style={{ color: colors.text }}>
                            {property.title}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1 text-[13px] font-medium" style={{ color: colors.textSecondary }}>
                        <MapPin className="w-3.5 h-3.5" />
                        {property.location}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="text-[14px] font-bold" style={{ color: colors.text }}>{property.bookings}</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="text-[14px] font-bold text-green-500">{property.revenue.toLocaleString()}</span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ color: '#EAB308' }}>
                        {property.rating.toFixed(1)} <span className="text-[10px]">★</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
